import { Tabs } from '@mantine/core';
import Head from 'next/head';
import { useRouter } from 'next/router';
import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { ClapperboardPlayBold, DocumentAdd } from 'solar-icons';
import { Container } from '@/DesignSystem/Component';
import { authSetIsTransitioningAction } from '@/Module/Auth/Store/Slice/AuthSlice';
import { APPLICATION_TITLE } from '@/Module/Core/Constant';
import { replaceHalfSpace } from '@/Module/Core/Helper';
import { DefaultLayoutBreadcrumb } from '@/Module/DefaultLayout/Component';
import { LessonListDescription, LessonSampleQuestion, LessonSectionList, LessonSectionListHeader } from '@/Module/Lesson/Component';
import type { ISampleQuestions } from '@/Module/Lesson/Constant';
import { SAMPLE_QUESTIONS } from '@/Module/Lesson/Constant';
import { useLessonSectionPage } from '@/Module/Lesson/Hook';
import type { IBookJson } from '@/Module/Lesson/Model/DataModel';
import { lessonSetActiveTabNumber } from '@/Module/Lesson/Store/Slice/LessonSlice';
import { useAppDispatch, useAppSelector } from '@/Module/Store/Hook';
import style from './BookPageContainer.module.scss';
interface IBookPageContainerProps {
  bookData: IBookJson;
}
const BookPagePageContainer: FC<IBookPageContainerProps> = ({
  bookData
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    breadcrumb
  } = useLessonSectionPage();
  const firstTabRef = useRef<HTMLButtonElement>(null);
  const lessonSectionTotalSize = useAppSelector(state => state.lessonSection.total);
  const activeTabNumber = useAppSelector(state => state.lesson.activeTabNumber);
  const hasVideos = useMemo(() => {
    return lessonSectionTotalSize > 0;
  }, [lessonSectionTotalSize]);
  const [hasSampleQuestions, sampleQuestions] = useMemo(() => {
    if (!bookData?.id) return [false, {} as unknown as ISampleQuestions[]];
    return [SAMPLE_QUESTIONS[Number(bookData.id)]?.length > 0, SAMPLE_QUESTIONS[Number(bookData.id)]];
  }, [bookData?.id]);
  const tabs: ReactNode[] = useMemo(() => {
    const result = [];
    if (hasVideos) {
      result.push(<Tabs.Tab key="ویدئوهای آموزشی" value="1" ref={firstTabRef} className="z-10 rounded-none bg-white ">
          <ClapperboardPlayBold />
          ویدئوهای آموزشی
        </Tabs.Tab>);
    }
    if (hasSampleQuestions) {
      result.push(<Tabs.Tab key="نمونه سوالات امتحانی" value="2" className="examCustom rounded-none">
          <DocumentAdd />
          نمونه سوالات امتحانی
        </Tabs.Tab>);
    }
    return result;
  }, [activeTabNumber, hasSampleQuestions]);
  const panel: ReactNode[] = useMemo(() => {
    const result: ReactNode[] = [];
    if (hasVideos) {
      result.push(<Tabs.Panel key="1" value="1">
          <LessonSectionList />
        </Tabs.Panel>);
    }
    if (hasSampleQuestions) {
      result.push(<Tabs.Panel value="2" key="2">
          <LessonSampleQuestion courseId={Number(bookData?.id)} sampleQuestions={sampleQuestions} />
        </Tabs.Panel>);
    }
    return result;
  }, [hasSampleQuestions]);
  useEffect(() => {
    dispatch(authSetIsTransitioningAction(false));
  }, [dispatch]);
  if (!bookData) return null;
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="BookPageContainer.tsx">
        <title>{replaceHalfSpace(bookData.seoTitle)}</title>
        {bookData.seoDescription && <meta name="description" content={bookData.seoDescription} />}
        <meta property="og:site_name" content={replaceHalfSpace(APPLICATION_TITLE)} data-sentry-element="meta" data-sentry-source-file="BookPageContainer.tsx" />
        <meta property="og:title" content={bookData.seoTitle} data-sentry-element="meta" data-sentry-source-file="BookPageContainer.tsx" />
        <meta property="og:type" content="website" data-sentry-element="meta" data-sentry-source-file="BookPageContainer.tsx" />
        {bookData.seoDescription && <meta name="og:description" content={bookData.seoDescription} />}
        <link rel="canonical" href={`https://ino.school${router.asPath}`} />
        <meta name="twitter:title" content={bookData.seoTitle} data-sentry-element="meta" data-sentry-source-file="BookPageContainer.tsx" />
        {bookData.seoDescription && <meta name="twitter:description" content={bookData.seoDescription} />}
      </Head>

      <DefaultLayoutBreadcrumb items={breadcrumb} data-sentry-element="DefaultLayoutBreadcrumb" data-sentry-source-file="BookPageContainer.tsx" />

      <Container data-sentry-element="Container" data-sentry-source-file="BookPageContainer.tsx">
        <LessonSectionListHeader title={bookData.title} description={bookData.summary} courseId={bookData.id} badgeTitle={bookData.subTitle} data-sentry-element="LessonSectionListHeader" data-sentry-source-file="BookPageContainer.tsx" />
        <div>
          <Tabs defaultValue={String(hasVideos ? activeTabNumber : 2)} onChange={value => dispatch(lessonSetActiveTabNumber(Number(value)))} classNames={style} data-sentry-element="Tabs" data-sentry-source-file="BookPageContainer.tsx">
            <Tabs.List className="mb-4" data-sentry-element="unknown" data-sentry-source-file="BookPageContainer.tsx">{tabs}</Tabs.List>
            {panel}
          </Tabs>
        </div>
        {/* <LessonSectionList /> */}
        <LessonListDescription description={bookData.description} activeTabNumber={activeTabNumber} data-sentry-element="LessonListDescription" data-sentry-source-file="BookPageContainer.tsx" />
      </Container>
    </>;
};
export default BookPagePageContainer;